import { Link, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Menu, X, Church, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const navItems = [
  {
    label: "Home",
    to: "/",
    children: [
      { to: "/about", label: "About Us" },
      { to: "/beliefs", label: "Beliefs" },
      { to: "/bible", label: "KJV Bible" },
      { to: "/donate", label: "Give" },
      { to: "/contact", label: "Contact" },
      { to: "/blog", label: "Blog" },
    ],
  },
  {
    label: "Church School",
    children: [
      { to: "/church-school-program", label: "Church School Program" },
      { to: "/start-a-church-school", label: "Start a School" },
      { to: "/partner-program", label: "Partner Program" },
    ],
  },
];

function DesktopDropdown({ item, pathname }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isChildActive = item.children?.some((c) => pathname === c.to);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-[#C8A97E] ${
          isChildActive || pathname === item.to ? "text-[#C8A97E]" : "text-[#595959]"
        }`}
      >
        {item.to ? (
          <Link to={item.to} onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
            {item.label}
          </Link>
        ) : (
          item.label
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-[#E5E2DC] py-1.5 min-w-[200px] z-50 animate-fade-in">
          {item.children.map((child) => (
            <Link
              key={child.to}
              to={child.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-link-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`block px-4 py-2.5 text-sm transition-colors hover:bg-[#FAF9F6] hover:text-[#C8A97E] ${
                pathname === child.to ? "text-[#C8A97E] bg-[#FAF9F6]" : "text-[#595959]"
              }`}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileDropdown({ item, pathname, onClose }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-[#E5E2DC]">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between py-3 text-base font-medium transition-colors hover:text-[#C8A97E] ${
          pathname === item.to || item.children?.some((c) => pathname === c.to)
            ? "text-[#C8A97E]"
            : "text-[#595959]"
        }`}
      >
        {item.to ? (
          <Link to={item.to} onClick={(e) => { e.stopPropagation(); onClose(); }}>
            {item.label}
          </Link>
        ) : (
          <span>{item.label}</span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-2 pl-4">
          {item.children.map((child) => (
            <Link
              key={child.to}
              to={child.to}
              onClick={onClose}
              className={`block py-2 text-sm transition-colors hover:text-[#C8A97E] ${
                pathname === child.to ? "text-[#C8A97E]" : "text-[#595959]"
              }`}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  return (
    <nav
      data-testid="navbar"
      className="bg-[#FAF9F6]/90 backdrop-blur-xl border-b border-[#E5E2DC] sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
        >
          <Church className="w-7 h-7 text-[#1C2E4A] group-hover:text-[#C8A97E] transition-colors" />
          <span className="font-serif text-xl md:text-2xl font-semibold text-[#1C2E4A] tracking-tight">
            ACS Virtual Church
          </span>
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex items-center gap-6">
          {navItems.map((item) =>
            item.children ? (
              <DesktopDropdown key={item.label} item={item} pathname={location.pathname} />
            ) : (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-medium transition-colors hover:text-[#C8A97E] ${
                  location.pathname === item.to ? "text-[#C8A97E]" : "text-[#595959]"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
          {user ? (
            <Link
              to="/members"
              data-testid="nav-link-members"
              className={`text-sm font-medium px-4 py-1.5 rounded-md transition-all ${
                location.pathname.startsWith("/members")
                  ? "bg-[#C8A97E] text-white"
                  : "bg-[#1C2E4A] text-white hover:bg-[#152238]"
              }`}
            >
              Members
            </Link>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                data-testid="nav-link-login"
                className="text-sm font-medium text-[#595959] hover:text-[#C8A97E] transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/register"
                data-testid="nav-link-join"
                className="text-sm font-medium bg-[#1C2E4A] text-white hover:bg-[#152238] px-4 py-1.5 rounded-md transition-colors"
              >
                Join Us
              </Link>
            </div>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[#1C2E4A] p-2"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#FAF9F6] border-t border-[#E5E2DC] px-6 pb-6 animate-fade-in"
        >
          {navItems.map((item) =>
            item.children ? (
              <MobileDropdown key={item.label} item={item} pathname={location.pathname} onClose={() => setOpen(false)} />
            ) : (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={`block py-3 text-base font-medium border-b border-[#E5E2DC] transition-colors hover:text-[#C8A97E] ${
                  location.pathname === item.to ? "text-[#C8A97E]" : "text-[#595959]"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
          <Link
            to={user ? "/members" : "/login"}
            onClick={() => setOpen(false)}
            className="block py-3 text-base font-medium text-[#C8A97E] border-b border-[#E5E2DC]"
          >
            {user ? "Members Area" : "Log In"}
          </Link>
          {!user && (
            <Link
              to="/register"
              onClick={() => setOpen(false)}
              className="block py-3 text-base font-medium text-[#1C2E4A] border-b border-[#E5E2DC]"
            >
              Join Us
            </Link>
          )}
          <Link
            to="/prayer-request"
            onClick={() => setOpen(false)}
            className="block py-3 text-base font-medium text-[#595959] border-b border-[#E5E2DC]"
          >
            Prayer Request
          </Link>
        </div>
      )}
    </nav>
  );
}
